import { useStyles } from "./styles";
import { useState, useEffect, useMemo, useContext } from "react";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import constants from "../../../constants/constants.json";
import PropsTypes from "prop-types";
import BasicTable from "components/table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import ModalPopup from "components/modalPopup";
import PermissionsSettings from "components/permissionsSettings";
import LoadingButton from "@mui/lab/LoadingButton";

import { useNavigate } from "react-router";
import { getToken } from "utils/loadAuth";
import { useAuth0 } from "@auth0/auth0-react";
import {
  fetchExternalPermissions,
  getPermissionByName,
  deletePermissionById,
  createExternalPermission,
} from "pages/userManagement/permissions/permission.service";

import Notification from "components/snackbar";
import { UserContext } from "context/userContext";
import SearchField from "components/simpleSearch";

const Permissions = ({ isNavbarOpen }) => {
  const theme = useTheme();
  const classes = useStyles({ theme, isNavbarOpen });
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [externalPermissions, setExternalPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const [openNotification, setOpenNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [copiedPermissions, setCopiedPermissions] = useState([]);
  const [severity, setSeverity] = useState("info");
  const userContextValue = useContext(UserContext) || {};
  const loggedInUserType = userContextValue?.loggedInUser?.user_type;

  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  const userPermissions = useMemo(
    () => user?.[constants.links.userPermissionsEndPoint] || [],
    [user]
  );

  const handleOpen = (url, row) => {
    setSelectedRow(row);
    navigate(url, {
      state: {
        activeTab: "permissions",
        permissionScope: row?.scope,
        permissionId: row?.id,
      },
    });
    setOpen(true);
  };

  const deletePermission = async (permissionId) => {
    setDeleteOpen(false);
    try {
      setDeleting(true);
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });
      await deletePermissionById({ token, permissionId });
      setExternalPermissions((prev) =>
        prev.filter((permission) => permission.id !== permissionId)
      );

      setSelectedRow(null);
      setOpenNotification(true);
      setSeverity("success");
      setMessage(`${selectedRow.scope} Permission deleted successfully`);
    } catch (error) {
      console.error("Error deleting permission:", error);
      setOpenNotification(true);
      setSeverity("error");
      setMessage(
        error?.response?.data?.message || "Failed to delete permission"
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      field: "scope",
      headerName: "scope",
      flex: 0.1,
    },

    {
      field: "scopeDesc",
      headerName: "description",
      flex: 0.07,
    },

    {
      field: "Actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.05,
      renderCell: (param) => {
        const { id, scope, scopeDesc, disabled } = param.row;
        return (
          <Box
            data-testid={"action-container"}
            className={classes.actionsContainer}
          >
            <Button
              data-testid={"edit-btn"}
              className={classes.actionBtn}
              disabled={disabled || deleting}
              onClick={() =>
                handleOpen(
                  "/usermanagement/permissions/editpermission?id=" + id,
                  {
                    id,
                    scope,
                    scopeDesc,
                  }
                )
              }
              title={constants.edit}
            >
              <EditIcon />
            </Button>

            <LoadingButton
              data-testid={"delete-btn"}
              className={classes.actionBtn}
              title={constants.delete}
              disabled={disabled || deleting}
              onClick={() => {
                setSelectedRow({ id, scope });
                setDeleteOpen(true);
              }}
              loading={deleting && selectedRow?.id === id}
            >
              <DeleteIcon />
            </LoadingButton>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        const token = await getToken({
          getAccessTokenSilently,
          loginWithRedirect,
        });
        const permissions = await fetchExternalPermissions({ token });
        if (loggedInUserType !== 1 && loggedInUserType !== 2) {
          const permissionsWithDisabled = permissions.map((permission) => ({
            ...permission,
            disabled: !userPermissions.includes(permission.scope),
          }));
          setExternalPermissions(permissionsWithDisabled);
          setCopiedPermissions(permissionsWithDisabled);
        } else {
          setExternalPermissions(permissions);
          setCopiedPermissions(permissions);
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    userPermissions,
    loggedInUserType,
  ]);

  const onSearchChange = (value) => {
    setSearch(value);
    if (!value) {
      setExternalPermissions(copiedPermissions);
      return;
    }
    const filteredPermissions = copiedPermissions.filter((permission) =>
      permission?.scope?.toLowerCase().includes(value.toLowerCase())
    );
    setExternalPermissions(filteredPermissions);
  };

  return (
    <>
      <Grid2
        container
        sx={{ width: "100%" }}
        data-testid="permissions-container"
      >
        <Grid2 size={2}></Grid2>
        {!open ? (
          <Grid2 size={10} className={classes.wrapper}>
            <Box className={classes.tableWrapper}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography className={classes.title}>
                  {constants.permissionsTitle}
                </Typography>
                <Button
                  onClick={() =>
                    handleOpen(
                      "/usermanagement/permissions/addpermission",
                      null
                    )
                  }
                  className={classes.addPermissionButton}
                  data-testid="add-permission-button"
                >
                  {constants.addPermission}
                </Button>
              </Box>
              <Stack className={classes.sectionsWrapper} spacing={1}>
                <Box sx={{ width: "100%" }}>
                  <SearchField
                    searchValue={search}
                    setSearchValue={(e) => onSearchChange(e)}
                    placeholderText={constants.searchPermissionByScope}
                  />
                  <BasicTable
                    showBorder={false}
                    disableColumnMenu
                    isLoading={loading}
                    columns={columns}
                    rows={externalPermissions}
                  />
                </Box>
              </Stack>
            </Box>
          </Grid2>
        ) : (
          <PermissionsSettings
            handleClose={() => {
              setOpen(false);
              setSelectedRow(null);
            }}
            permission={selectedRow}
            getPermissionByName={getPermissionByName}
            createExternalPermission={createExternalPermission}
            isNavbarOpen={isNavbarOpen}
            fetchToken={async () =>
              await getToken({
                getAccessTokenSilently,
                loginWithRedirect,
              })
            }
          />
        )}
        <Notification
          message={message}
          open={openNotification}
          setOpen={setOpenNotification}
          severity={severity}
        />
      </Grid2>
      <ModalPopup
        title={constants.deletePermission}
        message={
          <Typography sx={{ textAlign: "center", lineHeight: "1.6" }}>
            <strong>{selectedRow?.scope}</strong>{" "}
            {constants.deletePermissionMsg}
          </Typography>
        }
        primaryBtnText="Yes"
        primaryBtnAction={() => deletePermission(selectedRow?.id)}
        secondaryButtonText="No"
        open={deleteOpen}
        handleClose={() => {
          setSelectedRow(null);
          setDeleteOpen(false);
        }}
        secondarybtnAction={() => {
          setSelectedRow(null);
          setDeleteOpen(false);
        }}
      />
    </>
  );
};

Permissions.propTypes = {
  isNavbarOpen: PropsTypes.bool,
};

export default Permissions;
